<template>
  <div>
    <div class="relative">
      <div
        ref="expandableContainer"
        :class="{
          'max-h-28': !isExpanded && size === 'default',
          'max-h-20': !isExpanded && size === 'small',
          'max-h-10': !isExpanded && size === 'single-line',
        }"
        class="overflow-hidden"
      >
        <slot />
      </div>

      <div
        v-if="needExpand && !isExpanded"
        class="absolute bottom-0 left-0 right-0 pointer-events-none"
        :class="{
          'h-8': size === 'default',
          'h-6': size === 'small',
          'h-2.5': size === 'single-line'
        }"
        :style="gradientOverlayStyle"
      />
    </div>

    <div
      v-if="needExpand"
      class="flex justify-end"
    >
      <button
        type="button"
        class="text-primary px-3 py-1 rounded-lg font-medium cursor-pointer hover:underline transition-colors"
        :style="buttonBackgroundStyle"
        @click="toggleExpanded"
      >
        Voir {{ isExpanded ? 'moins' : 'plus' }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';

interface RGB {
    red: number;
    green: number;
    blue: number;
}

interface Props {
    backgroundColor?: RGB;
    size?: 'default' | 'small' | 'single-line';
}

const props = withDefaults(defineProps<Props>(), {
    backgroundColor: () => ({ red: 238, green: 236, blue: 247 }),
    size: 'default'
});

const isExpanded = defineModel<boolean>({required: false, default: false});
const isMounted = ref(false);
const expandableContainer = ref<HTMLElement | null>(null);

const gradientOverlayStyle = computed(() => {
    const { red, green, blue } = props.backgroundColor;
    return `background: linear-gradient(to bottom, rgba(${red}, ${green}, ${blue}, 0) 0%, rgba(${red}, ${green}, ${blue}, 0.8) 50%, rgb(${red}, ${green}, ${blue}) 100%);`;
});

const buttonBackgroundStyle = computed(() => {
    const { red, green, blue } = props.backgroundColor;
    return `background-color: rgb(${red}, ${green}, ${blue});`;
});

const needExpand = computed(() => {
    if (!isMounted.value || !expandableContainer.value) {
        return false;
    }

    return expandableContainer.value.scrollHeight > expandableContainer.value.clientHeight;
});

const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
};

onMounted(async () => {
    isMounted.value = true;
    await nextTick();
});
</script>
